// Display mirror of portal/backend/lib/report-types.mjs and compliance-tier.mjs (ENTRAID-341). The API enforces type,
// role and tier on every route; this only decides what the page offers.
import { complianceAllowedForTier } from './compliance-tier.js';

export const REPORT_TYPES = Object.freeze(['compliance-readiness', 'security-posture']);
export const POSTURE_SECTIONS = Object.freeze(['onguard-score', 'secure-score']);
export const MAX_FRAMEWORKS = 8;

// Types a person generates from the dialog (REPORT_TYPES), and the types the Reports list shows: an Access review report
// is created when a campaign closes (Access reviews page), never from the dialog (spec 2026-10-26, section 9).
// The Implementation footprint PDF is made from its own page (Implementation footprint), so it is listed here and never in the dialog.
// The Licenses usage PDF likewise is made from the Licenses page (Hugo Andrade, 2026-10-30).
export const LISTED_TYPES = Object.freeze([...REPORT_TYPES, 'access-review', 'implementation-footprint', 'licenses-usage']);
const LABELS = Object.freeze({ 'compliance-readiness': 'Compliance readiness', 'security-posture': 'Security posture', 'access-review': 'Access review', 'implementation-footprint': 'Implementation footprint', 'licenses-usage': 'Licenses usage' });
/** What an Access review report evidences (spec 9.2: NIST SP 800-53 Rev. 5 only; AC-6(7) exact, the others supporting). */
export const ACCESS_REVIEW_CONTROLS_TEXT = 'NIST SP 800-53 Rev. 5 AC-6(7) review of user privileges; supports AC-2(j), AC-6 and AC-6(5)';
const BLURBS = Object.freeze({
  'compliance-readiness': 'Readiness against the benchmarks you choose, with evidence.',
  'security-posture': 'Where your tenant stands today: onGuard score, findings and Microsoft Secure Score.',
  'access-review': 'A closed privileged access review: every decision, every removal verified, locked as evidence.',
  'implementation-footprint': 'What onGuard runs in your tenant and AWS account: sources, paths, schedule and features.',
  'licenses-usage': 'How your Microsoft licenses follow the license best practices: waste, unused products, seats and savings.',
});
/**
 * The Reports query that opens the generate dialog on one type (plan C2b: Security's "Create report"). The Reports page
 * opens it only for an account that may generate that type and then drops the query; the API still decides.
 */
export const GENERATE_PARAM = 'generate';
export function generateReportPath(reportsPath, type) {
  if (typeof reportsPath !== 'string' || !REPORT_TYPES.includes(type)) return null;
  return `${reportsPath}?${GENERATE_PARAM}=${encodeURIComponent(type)}`;
}

export const typeLabel = (id) => LABELS[id] ?? '';
export const typeBlurb = (id) => BLURBS[id] ?? '';

export const COMPLIANCE_TIER_REASON = 'Compliance readiness is included from the Professional tier.';

/**
 * The types the generate dialog offers. Security Posture is included in every tier (decision D2b); Compliance Readiness
 * needs Professional or Enterprise. Unknown tier means starter. `postureEnabled` stays a switch for a build that must not offer it.
 */
export function typesAvailable({ tier, postureEnabled = true }) {
  const out = [];
  if (complianceAllowedForTier(tier)) out.push('compliance-readiness');
  if (postureEnabled) out.push('security-posture');
  return out;
}

/** The types the dialog shows disabled, each with the reason, so a customer below the tier sees what the next tier adds. */
export function typesUnavailable({ tier }) {
  return complianceAllowedForTier(tier) ? [] : [{ id: 'compliance-readiness', reason: COMPLIANCE_TIER_REASON }];
}

export const SECTION_LABELS = Object.freeze({ 'onguard-score': 'onGuard score and findings', 'secure-score': 'Microsoft Secure Score' });
export const SECTION_HINTS = Object.freeze({
  'onguard-score': 'onGuard score in detail, with findings',
  'secure-score': 'Microsoft Secure Score in detail, with control profiles and recommended actions',
});
export const sectionsText = (sections) => (Array.isArray(sections) ? sections.map((s) => SECTION_LABELS[s]).filter(Boolean).join(', ') : '');
